'use client';
import React, { Suspense } from 'react';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import { SectionTitle } from '@benzinga/core-ui';
import type { AnalystRatingsSummary } from '@benzinga/calendar-manager';
import { formatPrice, formatUpsideDownside } from '@benzinga/calendars';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import type { BullBearStatements } from '@benzinga/quotes-manager';

import type { BaseQuotePageProps, RatingsByMonthWithCounts } from '../../../../../src/utils/quote/types';

import QuoteCalendar from '../../../../../src/components/Quote/QuoteCalendar';
import DrilldownFooter from '../../../../../src/components/Quote/DrilldownFooter';
import AnalystRatingsFAQ, { stockTrend } from '../../../../../src/components/Quote/FAQ/AnalystRatingsFAQ';
import ZacksHeadScript from '../../../../../src/components/Quote/ZacksHeadScript';
import { AnalystRatingSummary, AnalystRatingSummaryBoxes } from '../../../../../src/components/Quote/AnalystRatingSummary';
import { FullDisplayBullVsBear } from '../../../../../src/components/Quote/BullVsBear';
import { QuoteFooter } from '../../../../../src/components/Quote/QuoteFooter';

const RaptiveAdPlaceholder = React.lazy(() =>
  import('@benzinga/ads').then(module => {
    return { default: module.RaptiveAdPlaceholder };
  }),
);

interface AnalystRatingsContentProps extends BaseQuotePageProps {
  ratingsSummary: AnalystRatingsSummary | null;
  bullSayBearSay: BullBearStatements | null;
  ratingsByMonthWithCounts: RatingsByMonthWithCounts[];
}

const AnalystRatingsContent: React.FC<AnalystRatingsContentProps> = ({
  metaProps,
  profile,
  bullSayBearSay,
  ratingsByMonthWithCounts,
  ratingsSummary,
  symbol,
}) => {
  const { t, i18n } = useTranslation(['quote', 'common']);

  const lastThreeRatings = (ratingsSummary?.ratings ?? [])
    .filter(rating => {
      const upsideDownside = formatUpsideDownside(
        Number(rating?.quote?.last ?? rating?.quote?.lastTradePrice),
        rating.pt_current,
      );
      return upsideDownside !== '';
    })
    .slice(0, 3);

  const formatDate = (date: string) =>
    DateTime.fromFormat(date, 'yyyy-MM-dd').setLocale(i18n.language).toFormat('MMMM d, yyyy');

  const generatePriceTargetAndAnalystSummary = (): string | null => {
    if (!lastThreeRatings[0]) return null;
    const lastTradePrice = lastThreeRatings[0]?.quote?.lastTradePrice;
    const mostRecentFirmNames = lastThreeRatings.map(rating => rating.analyst);
    const areAllMostRecentFirmNamesTheSame = mostRecentFirmNames.every(name => name === mostRecentFirmNames[0]);
    const mostRecentFirmDates = lastThreeRatings.map(rating => rating.date);

    const listFormatter = new Intl.ListFormat(i18n.language, {
      style: 'long',
      type: 'conjunction',
    });
    const analystRatingsCompanies = listFormatter.format(mostRecentFirmNames);
    const analystRatingsDates = mostRecentFirmDates.map(date => {
      const formattedDate = formatDate(date);
      return formattedDate;
    });

    const analystRatingsDateText = analystRatingsDates.every(date => date === analystRatingsDates[0])
      ? analystRatingsDates[0]
      : listFormatter.format(analystRatingsDates);

    const combinedPriceTargetFromLastThreeRatings = lastThreeRatings.reduce(
      (acc, rating) => acc + (rating.pt_current ? Number(rating.pt_current) : 0),
      0,
    );
    const averagePriceTargetFromLastThreeRatings = combinedPriceTargetFromLastThreeRatings / lastThreeRatings.length;

    const getAveragePriceTargetPercentageChange = (): string | null => {
      if (!lastTradePrice) return null;
      let change = (averagePriceTargetFromLastThreeRatings - lastTradePrice) * 100;
      change = change / lastTradePrice;
      return change.toFixed(2);
    };

    const getAverageTickerDirection = (): string | null => {
      if (!lastTradePrice) return null;
      const result = averagePriceTargetFromLastThreeRatings > lastTradePrice ? stockTrend.upside : stockTrend.downside;
      return result;
    };

    const averagePriceTargetPercentageChange = getAveragePriceTargetPercentageChange();
    const averageTickerDirection = getAverageTickerDirection();

    const companyName = profile?.richQuoteData?.companyStandardName;
    const consensusPriceTarget = formatPrice(ratingsSummary?.consensusPriceTarget ?? 0);
    const numRatings = ratingsSummary?.numUniqueRatings;
    const numLastThreeRatings = lastThreeRatings.length;
    const ratingsCompanies = areAllMostRecentFirmNamesTheSame ? mostRecentFirmNames[0] : analystRatingsCompanies;
    const ratingsDate = analystRatingsDateText;
    const averagePriceTarget = formatPrice(averagePriceTargetFromLastThreeRatings);
    const averagePercentageChange = averagePriceTargetPercentageChange;
    const tickerDirection = averageTickerDirection;

    const highestPriceTarget = ratingsSummary?.highestPriceRating?.price
      ? formatPrice(ratingsSummary.highestPriceRating.price)
      : null;
    const highestPriceTargetFirm = ratingsSummary?.highestPriceRating?.firm || null;
    const highestPriceTargetDate = ratingsSummary?.highestPriceRating?.date
      ? formatDate(ratingsSummary.highestPriceRating.date)
      : null;

    const lowestPriceTarget = ratingsSummary?.lowestPriceRating?.price
      ? formatPrice(ratingsSummary.lowestPriceRating.price)
      : null;
    const lowestPriceTargetFirm = ratingsSummary?.lowestPriceRating?.firm;
    const lowestPriceTargetDate = ratingsSummary?.lowestPriceRating?.date
      ? formatDate(ratingsSummary.lowestPriceRating.date)
      : null;

    const result: string = t('Quote.Paragraphs.analyst-ratings', {
      averagePercentageChange: averagePercentageChange ?? '-',
      averagePriceTarget,
      companyName,
      consensusPriceTarget,
      highestPriceTarget,
      highestPriceTargetDate,
      highestPriceTargetFirm,
      lowestPriceTarget,
      lowestPriceTargetDate,
      lowestPriceTargetFirm,
      numLastThreeRatings,
      numRatings,
      ratingsCompanies,
      ratingsDate,
      tickerDirection,
    });
    return result;
  };

  const priceTargetAndAnalystSummary = generatePriceTargetAndAnalystSummary();

  return (
    <>
      <ZacksHeadScript />
      <div className="main-div flex flex-col gap-y-8">
        <div className="flex flex-col lg:flex-row">
          <div className="flex flex-col">
            <div className="w-full md:flex">
              <div className="w-full">
                {ratingsSummary && <AnalystRatingSummaryBoxes ratingsSummary={ratingsSummary} />}
                <FullDisplayBullVsBear profile={profile} statements={bullSayBearSay ?? null} />
                <div className="flex flex-col gap-2 mt-4">
                  <SectionTitle level={1} uppercase={false}>
                    {metaProps.title}
                  </SectionTitle>
                  <p
                    className="section-description"
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHTML(priceTargetAndAnalystSummary || metaProps.description),
                    }}
                  />
                </div>
              </div>
              <Suspense fallback={<div />}>
                <RaptiveAdPlaceholder
                  className="w-[300px] min-w-[300px] h-[250px] md:ml-4"
                  disableMargins={true}
                  onlyDesktop={true}
                  type="static-sidebar"
                />
              </Suspense>
            </div>
            <div className="flex flex-col gap-y-8">
              {ratingsSummary && (
                <AnalystRatingSummary
                  ratingsByMonthWithCounts={ratingsByMonthWithCounts}
                  ratingsSummary={ratingsSummary}
                />
              )}
              <div className="flex flex-col">
                <SectionTitle className="mb-2" level={3}>
                  {t('Quote.AnalystRatings.analyst-ratings-for', { name: profile?.richQuoteData?.name })}
                </SectionTitle>
                <QuoteCalendar calendar="analyst-ratings" initialData={ratingsSummary?.ratings ?? []} symbol={symbol} />
              </div>
            </div>
          </div>
        </div>
        <RaptiveAdPlaceholder className="w-[300px] min-w-[300px]" onlyMobile={true} type="content-small" />
        <DrilldownFooter
          isETF={profile.isETF}
          leftSection={
            <>
              <SectionTitle level={3} size="2xl">
                {t('Quote.FAQ.faq')}
              </SectionTitle>
              {ratingsSummary && profile?.richQuoteData && (
                <AnalystRatingsFAQ quote={profile?.richQuoteData} ratingsSummary={ratingsSummary} />
              )}
            </>
          }
          peers={profile.peers}
          peerSymbols={profile.peerSymbols}
          symbol={symbol}
          type={profile.schedule?.type ?? null}
        />
        <QuoteFooter activeTab="analyst-ratings" />
      </div>
    </>
  );
};

export default AnalystRatingsContent;
